/**
 * /orgs/:orgId/cost — what the organization's runs cost in a period.
 *
 * Read from rollups, so the page is cheap whatever the task count. Every
 * total carries its reported and rated shares, and the gap banner is the
 * point of the page: a usage type with no rate, or receipts with no exchange
 * rate, look exactly like something cheap, and a total that hides them is
 * wrong for the operator and the tenant alike. Ingestion jobs have no
 * capability and are shown as their own row.
 */
import { exportCostEvents, getCostRollups } from '@optum/api-client/endpoints/cost';
import type { PeriodCost } from '@optum/api-client/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { absoluteDate, quantity, thb } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { useRequiredOrgId } from '../../lib/use-current-org.js';
import { DataTable } from '../../ui/DataTable.js';
import { ListShape } from '../../ui/shapes.js';
import { SectionHeader } from '../../ui/SectionHeader.js';
import { StatRow } from '../../ui/StatRow.js';
import { Badge } from '../../ui/finixt.js';
import { GapBanner, GapChips, sharePercent } from '../cost/GapBanner.js';
import { ExportMenu } from '../cost/ExportMenu.js';
import { PeriodPicker } from '../cost/PeriodPicker.js';
import { monthOf, monthRange } from '../cost/period.js';

export function OrgCostPage() {
  const orgId = useRequiredOrgId();
  const [month, setMonth] = useState(() => monthOf(new Date()));
  const { start, end } = monthRange(month);
  const period = useQuery({
    queryKey: qk.costRollups(orgId, start, end),
    queryFn: () => getCostRollups({ organization_id: orgId, start, end }),
  });

  return (
    <ListShape
      title="Cost"
      subtitle="What this organization's runs cost: the vendors' receipts where they exist, the platform's rate card where they do not, by capability and by day."
      action={
        <ExportMenu
          storageKey="organization"
          run={(columns) => exportCostEvents({ start, end, organization_id: orgId }, columns)}
        />
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <PeriodPicker value={month} onChange={setMonth} />
        {period.isPending && <LoadingBlock />}
        {period.isError && (
          <ErrorBlock
            message={period.error instanceof Error ? period.error.message : 'Failed to load'}
          />
        )}
        {period.data && <PeriodView period={period.data} />}
      </div>
    </ListShape>
  );
}

function PeriodView({ period }: { period: PeriodCost }) {
  const organization = period.by_capability.length ? period.priced_total : '0';
  if (period.by_capability.length === 0) {
    return <EmptyBlock>No usage was recorded in this period.</EmptyBlock>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <GapBanner gaps={period} />
      <StatRow
        tiles={[
          { label: 'Cost', value: thb(organization), icon: 'payments' },
          {
            label: 'Of which reported',
            value: `${thb(period.reported_share)} · ${sharePercent(period.reported_share, period.priced_total)}`,
            icon: 'receipt',
          },
          {
            label: 'Events',
            value: quantity(period.by_capability.reduce((n, c) => n + c.events, 0)),
            icon: 'receipt_long',
          },
          { label: 'Days with usage', value: String(period.by_day.length), icon: 'calendar_month' },
        ]}
      />
      <section style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        <SectionHeader title="By capability" subtitle="Ingestion jobs are their own row." />
        <DataTable
          rows={period.by_capability}
          rowKey="capability_id"
          columns={[
            { key: 'capability_id', label: 'Capability', mono: true, strong: true },
            { key: 'events', label: 'Events', align: 'right', mono: true },
            {
              key: 'amount',
              label: 'Amount',
              align: 'right',
              mono: true,
              render: (row) => thb(row.amount),
            },
            {
              key: 'reported_share',
              label: 'Reported',
              align: 'right',
              mono: true,
              render: (row) => thb(row.reported_share),
            },
            {
              key: 'rated_share',
              label: 'Rated',
              align: 'right',
              mono: true,
              render: (row) => thb(row.rated_share),
            },
            {
              key: 'gaps',
              label: 'Gaps',
              render: (row) => <GapChips gaps={row} Chip={AmberChip} />,
            },
          ]}
        />
      </section>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        <SectionHeader
          title="By day"
          subtitle="Cost per usage type, reported and rated together."
        />
        <DataTable
          rows={period.by_day}
          rowKey="day"
          columns={[
            { key: 'day', label: 'Day', mono: true, render: (day) => absoluteDate(day.day) },
            {
              key: 'by_type',
              label: 'By type',
              wrap: true,
              render: (day) => (
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {Object.entries(day.by_type).map(([type, amount]) => (
                    <Badge tone="info">{`${type} ${thb(amount)}`}</Badge>
                  ))}
                </span>
              ),
            },
            {
              key: 'amount',
              label: 'Amount',
              align: 'right',
              mono: true,
              render: (day) => thb(day.amount),
            },
          ]}
        />
      </section>
    </div>
  );
}

function AmberChip({ label }: { label: string }) {
  return <Badge tone="warn">{label}</Badge>;
}
